import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, GBP, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { StatusBadge, UrgentTag } from "@/components/StatusBadge";
import { Hammer, Upload, AlertTriangle, CreditCard, FileText, ArrowUpRight } from "lucide-react";

function Stat({ label, value, sub }) {
  return (
    <div className="panel rounded-sm p-5">
      <div className="eyebrow mb-2">{label}</div>
      <div className="text-3xl font-semibold tracking-tight text-[#F5F3EF] num">{value}</div>
      {sub && <div className="text-xs text-[#8C867A] mt-1">{sub}</div>}
    </div>
  );
}

export default function MemberDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/dashboard/member").then((r) => setD(r.data)).catch(() => {}); }, []);

  if (!d) return <div className="text-[#8C867A]">Loading…</div>;
  const m = d.member;
  const first = user?.name?.split(" ")[0] || "there";

  const actions = [
    ["New Trade Job", Hammer, () => navigate("/jobs/new"), true],
    ["Upload Content", Upload, () => navigate("/content"), false],
    ["Report Equipment Issue", AlertTriangle, () => navigate("/workshop"), false],
    ["View Membership", CreditCard, () => navigate("/membership"), false],
    ["View Documents", FileText, () => navigate("/documents"), false],
  ];

  return (
    <div>
      <PageHeader eyebrow={`${m.bench_number} · Hatton Garden`} title={`Welcome, ${first}`} />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4 rise">
        <Stat label="Membership" value={<span style={{ color: "#34D399" }}>{m.status}</span>} sub={GBP(m.monthly_fee) + " / month"} />
        <Stat label="Security Deposit" value={<span className="brass-text">{GBP(m.security_deposit)}</span>} sub={`Held in trust · ${m.deposit_status}`} />
        <Stat label="Next Payment" value={<span className="text-2xl">{fmtDate(m.next_payment_date)}</span>} />
        <Stat label="Active Jobs" value={d.active_jobs} sub={`${d.jobs_ready} ready to collect`} />
        <Stat label="Content Pending" value={d.content_pending} sub="awaiting approval" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 panel rounded-sm p-5 rise">
          <div className="flex items-center justify-between mb-4">
            <div className="eyebrow">Recent Trade Jobs</div>
            <button onClick={() => navigate("/jobs")} className="text-xs brass-text link-underline flex items-center gap-1">View all <ArrowUpRight size={12} /></button>
          </div>
          <div className="divide-y" style={{ borderColor: "#28282D" }}>
            {d.recent_jobs.length === 0 && <div className="text-sm text-[#8C867A] py-6 text-center">No jobs yet. Create your first trade job.</div>}
            {d.recent_jobs.map((j) => (
              <button key={j.id} onClick={() => navigate(`/jobs/${j.id}`)} data-testid={`member-job-${j.id}`}
                className="w-full flex items-center justify-between gap-3 py-3.5 text-left hover:bg-[#1A1A1E] px-2 -mx-2 rounded-sm transition-colors">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs brass-text">{j.job_number}</span>
                    {j.priority === "URGENT" && <UrgentTag />}
                  </div>
                  <div className="text-sm text-[#E2DFD7] truncate mt-0.5">{j.title}</div>
                </div>
                <StatusBadge status={j.status} />
              </button>
            ))}
          </div>
        </div>

        <div className="panel rounded-sm p-5 rise">
          <div className="eyebrow mb-4">Quick Actions</div>
          <div className="flex flex-col gap-2">
            {actions.map(([label, Icon, fn, primary]) => (
              <button key={label} onClick={fn} data-testid={`quick-${label.toLowerCase().replace(/\s/g, "-")}`}
                className={`flex items-center gap-3 px-4 py-3.5 rounded-sm text-sm transition-all ${primary ? "btn-brass" : "panel hover:border-[#C5A059] text-[#E2DFD7]"}`}>
                <Icon size={17} /> {label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
